import * as THREE from 'three'
import Experience from '../../Experience.js'
import * as CANNON from 'cannon-es'
import { RectAreaLightHelper } from 'three/examples/jsm/helpers/RectAreaLightHelper.js'
import { Vec3 } from 'cannon-es'

export default class Forklift {
    constructor() {
        this.experience = new Experience()
        this.scene = this.experience.scene
        this.resources = this.experience.resources
        this.physics = this.experience.physics
        this.controls = this.experience.controls
        this.time = this.experience.time
        this.scene_dimensions = this.experience.world.scene_dimensions

        this.rack = {}
        this.rack.up = false
        this.rack.down = false
        this.brake_mode = false
        this.brake_color = 0xff0000
        this.back_mode = false
        this.back_color = 0xFFEFAB
        this.rack.x = -1.5
        this.rack.y = -0.47
        this.rack.z = 0.08
        this.rack.max_y = 1
        this.rack.min_y = -0.47
        this.rack.step = 0.01
        this.colors = {
            red: 0xad2c2c,
            green: 0x379b3a,
            blue: 0x37619b,
            yellow: 0xcc8109,
        } 

        this.setModel()
        this.setWarningLight()
        this.setBrakeLights()
        this.setFrontLights()
        this.setPhysics()
        this.setColorChoiceListeners()
    }

    setModel() {
        this.model = new THREE.Group()
        this.model_body = this.resources.items.forkliftModel.clone()
        this.model_rack = this.resources.items.forkliftRackModel.clone()

        this.model.add(this.model_body)
        this.model.add(this.model_rack)
        this.model.scale.set(1.5, 1.5, 1.5)

        // Pivote model
        this.model.rotation.x = - Math.PI / 2

        // Get model outer dimensions
        let bbox = new THREE.Box3().setFromObject(this.model);
        this.dimensions = bbox.getSize(new THREE.Vector3());

        this.scene.add(this.model)
    }

    setWarningLight() {
        // Set mesh
        this.warning_light = new THREE.Group()
        const base = new THREE.Mesh(
            new THREE.CylinderGeometry(0.06, 0.06, 0.02, 32),
            new THREE.MeshStandardMaterial({ color: 0x000000 })
        )
        const light = new THREE.Mesh(
            new THREE.CapsuleGeometry(0.05, 0.05, 8, 12),
            new THREE.MeshPhysicalMaterial({
                color: 0xD94E04,
                metalness: 0,
                roughness: 0,
                transmission: 0.4,
                thickness: 0.2,
            })
        )
        light.position.set(0, 0.04, 0)
        this.warning_light.add(base)
        this.warning_light.add(light)
        this.warning_light.position.set(0.85, 0.25, 0.25)
        this.model.add(this.warning_light)

        // Set light
        this.warning_light_light = new THREE.RectAreaLight(0xD94E04, 0, 0.1, 0.1)
        this.warning_light_light.position.set(0, 0.05, 0)
        this.warning_light.add(this.warning_light_light)

        // Light helper
        this.warning_light_light_helper = new RectAreaLightHelper(this.warning_light_light)
        this.scene.add(this.warning_light_light_helper)
    }

    setFrontLights() {
        // Set mesh
        this.front_light = new THREE.Group()
        const base = new THREE.Mesh(
            new THREE.BoxGeometry(0.03, 0.1, 0.62),
            new THREE.MeshStandardMaterial({ color: 0x080808 })
        )

        this.front_light.add(base)
        this.front_light.position.set(-0.099, 0.8, 0.052)
        this.model.add(this.front_light)

        // Set lights
        this.front_light_light = new THREE.RectAreaLight(0xffffff, 0.2, 0.7, 0.09)
        this.front_light_light.position.set(-0.016, 0, 0)
        this.front_light_light.rotation.y = Math.PI / 2
        this.front_light.add(this.front_light_light)

        // Light helper
        this.front_light_light_helper = new RectAreaLightHelper(this.front_light_light)
        this.scene.add(this.front_light_light_helper)
    }

    setBrakeLights() {
        // Set mesh
        this.brake_light = new THREE.Group()
        this.brake_light.position.set(0.8, 0.25, 0.05)
        this.model.add(this.brake_light)

        // Set lights
        this.brake_light_light_left = new THREE.RectAreaLight(this.brake_color, 0.1, 0.15, 0.15)
        this.brake_light_light_left.position.set(0.184, -0.2, 0.25)
        this.brake_light_light_left.rotation.y = - Math.PI / 2
        this.brake_light_light_left.rotation.x = - Math.PI / 2
        this.brake_light_light_left.rotation.y = - Math.PI * 0.52
        this.brake_light.add(this.brake_light_light_left)

        this.brake_light_light_right = new THREE.RectAreaLight(this.brake_color, 0.1, 0.15, 0.15)
        this.brake_light_light_right.position.set(0.184, -0.2, -0.25)
        this.brake_light_light_right.rotation.y = - Math.PI / 2
        this.brake_light_light_right.rotation.x = - Math.PI / 2
        this.brake_light_light_right.rotation.y = - Math.PI * 0.52
        this.brake_light.add(this.brake_light_light_right)

        // Light helpers
        this.brake_light_light_left_helper = new RectAreaLightHelper(this.brake_light_light_left)
        this.brake_light_light_right_helper = new RectAreaLightHelper(this.brake_light_light_right)
        this.scene.add(this.brake_light_light_left_helper)
        this.scene.add(this.brake_light_light_right_helper)
    }

    setPosition(x, z) {
        this.model.position.set(x, 0.2, z)
        this.chassis.body.position.set(x, 0.2, z)
    }

    setPhysics() {
        this.setMaterials()
        this.setPhysicsBody()
    }

    setMaterials() {
        this.groundMaterial = new CANNON.Material("groundMaterial");
        this.wheelMaterial = new CANNON.Material("wheelMaterial");
        let wheelGroundContactMaterial = window.wheelGroundContactMaterial = new CANNON.ContactMaterial(this.wheelMaterial, this.groundMaterial, {
            friction: 0.3,
            restitution: 100,
            contactEquationStiffness: 1000,
        });

        // We must add the contact materials to the world
        this.physics.world.addContactMaterial(wheelGroundContactMaterial);
    }

    setPhysicsBody() {
        this.createGround()
        this.initParams()
        this.setOptions()
        this.createVehicle()
        this.createWheels()
        this.setKeyBindings()

        this.physics.objectsToUpdate.push({
            /* This model with rotation */
            mesh: this.model,
            body: this.chassis.body
        })
    }

    initParams() {
        this.parameters = {}
        this.parameters.maxSteerVal = 0.6
        this.parameters.maxForce = 280
        this.parameters.brakeForce = 10
    }

    createGround() {
        const groundShape = new CANNON.Box(new CANNON.Vec3((this.scene_dimensions.width + 5) / 2, 0.25, (this.scene_dimensions.depth + 5) / 2))

        const groundBody = new CANNON.Body({
            mass: 0,
            material: this.groundMaterial
        })
        groundBody.addShape(groundShape)
        groundBody.position.set(this.scene_dimensions.width / 2, - 0.75, this.scene_dimensions.depth / 2)
        this.physics.world.addBody(groundBody)
    }

    setOptions() {
        this.options = {}
        this.options.radius = 0.35
        this.options.directionLocal = new CANNON.Vec3(0, -1, 0)
        this.options.wheelOffsetWidthLeft = 0.63
        this.options.wheelOffsetWidthRight = 0.48
        this.options.wheelOffsetHeight = -0.12
        this.options.wheelOffestFront = -0.55
        this.options.wheelOffestBack = 1.04
        this.options.suspensionStiffness = 30
        this.options.suspensionRestLength = 0.3
        this.options.frictionSlip = 1.4
        this.options.dampingRelaxation = 2.3
        this.options.dampingCompression = 4.4
        this.options.maxSuspensionForce = 100000
        this.options.rollInfluence = 0.01
        this.options.axleLocal = new CANNON.Vec3(0, 0, 1)
        this.options.chassisConnectionPointLocal = new CANNON.Vec3(-1, 0, 1)
        this.options.maxSuspensionTravel = 0.3
        this.options.customSlidingRotationalSpeed = -30
        this.options.useCustomSlidingRotationalSpeed = true
    }

    createVehicle() {
        this.chassis = {}

        this.chassis.shape = new CANNON.Box(new CANNON.Vec3(1.2, 0.5, 0.55))
        this.chassis.body = new CANNON.Body({ mass: 300 })
        this.chassis.rack = new CANNON.Box(new CANNON.Vec3(0.5, 0.02, 0.32))
        this.chassis.body.addShape(this.chassis.shape, new CANNON.Vec3(0.3, 0, 0.1))
        this.chassis.body.addShape(this.chassis.rack, new CANNON.Vec3(this.rack.x, this.rack.y, this.rack.z))
        this.chassis.body.position.set(0, 2, 0)
        this.chassis.body.angularVelocity.set(0, 0.5, 2)

        // Create the vehicle
        this.vehicle = new CANNON.RaycastVehicle({
            chassisBody: this.chassis.body
        })

        this.vehicle.addToWorld(this.physics.world)
    }

    createWheels() {
        this.options.chassisConnectionPointLocal.set(this.options.wheelOffestFront, this.options.wheelOffsetHeight, this.options.wheelOffsetWidthLeft)
        this.vehicle.addWheel(this.options)

        this.options.chassisConnectionPointLocal.set(this.options.wheelOffestFront, this.options.wheelOffsetHeight, -this.options.wheelOffsetWidthRight)
        this.vehicle.addWheel(this.options)

        this.options.chassisConnectionPointLocal.set(this.options.wheelOffestBack, this.options.wheelOffsetHeight, this.options.wheelOffsetWidthLeft)
        this.vehicle.addWheel(this.options)

        this.options.chassisConnectionPointLocal.set(this.options.wheelOffestBack, this.options.wheelOffsetHeight, -this.options.wheelOffsetWidthRight)
        this.vehicle.addWheel(this.options)

        this.wheelBodies = []
        this.wheels = []
        // const wheelMaterial = new CANNON.Material('wheel')
        this.vehicle.wheelInfos.forEach((wheel, index) => {
            const cylinderShape = new CANNON.Cylinder(wheel.radius, wheel.radius, wheel.radius / 2, 50)
            const wheelBody = new CANNON.Body({
                mass: 0,
                material: this.wheelMaterial,
            })
            wheelBody.type = CANNON.Body.KINEMATIC
            wheelBody.collisionFilterGroup = 0 // turn off collisions
            const quaternion = new CANNON.Quaternion().setFromEuler(-Math.PI / 2, 0, 0)
            wheelBody.addShape(cylinderShape, new CANNON.Vec3(), quaternion)
            this.wheelBodies.push(wheelBody)
            this.physics.world.addBody(wheelBody)

            // Create a wheel model for each wheel
            let wheelModel
            if (index % 2 === 0) {
                wheelModel = this.resources.items.forkliftWheelLeftModel.clone()
            } else {
                wheelModel = this.resources.items.forkliftWheelRightModel.clone()
            }
            wheelModel.scale.set(1.5, 1.5, 1.5)

            this.wheels.push(wheelModel)
            this.scene.add(wheelModel)

            // Set the wheel model position
            wheelModel.position.copy(wheelBody.position)
            wheelModel.quaternion.copy(wheelBody.quaternion)
        })

        // Update the wheel bodies
        this.physics.world.addEventListener('postStep', () => {
            for (let i = 0; i < this.vehicle.wheelInfos.length; i++) {
                this.vehicle.updateWheelTransform(i)
                const transform = this.vehicle.wheelInfos[i].worldTransform
                const wheelBody = this.wheelBodies[i]
                wheelBody.position.copy(transform.position)
                wheelBody.quaternion.copy(transform.quaternion)

                // Update the wheel model
                this.wheels[i].position.copy(wheelBody.position)
                this.wheels[i].quaternion.copy(wheelBody.quaternion)
            }
        })
    }

    setKeyBindings() {
        // Keybindings
        // Add force on keydown
        document.addEventListener('keydown', (event) => {
            switch (event.code) {
                case 'ArrowUp':
                    this.vehicle.applyEngineForce(-this.parameters.maxForce, 0)
                    this.vehicle.applyEngineForce(-this.parameters.maxForce, 1)
                    document.getElementById('control-up').classList.add('active')
                    break

                case 'ArrowDown':
                    this.vehicle.applyEngineForce(this.parameters.maxForce, 0)
                    this.vehicle.applyEngineForce(this.parameters.maxForce, 1)
                    document.getElementById('control-down').classList.add('active')
                    this.back_mode = true
                    break

                case 'ArrowLeft':
                    this.vehicle.setSteeringValue(-this.parameters.maxSteerVal, 2)
                    this.vehicle.setSteeringValue(-this.parameters.maxSteerVal, 3)
                    document.getElementById('control-left').classList.add('active')
                    break

                case 'ArrowRight':
                    this.vehicle.setSteeringValue(+this.parameters.maxSteerVal, 2)
                    this.vehicle.setSteeringValue(+this.parameters.maxSteerVal, 3)
                    document.getElementById('control-right').classList.add('active')
                    break

                case 'Space':
                    this.vehicle.setBrake(this.parameters.brakeForce, 0)
                    this.vehicle.setBrake(this.parameters.brakeForce, 1)
                    this.vehicle.setBrake(this.parameters.brakeForce, 2)
                    this.vehicle.setBrake(this.parameters.brakeForce, 3)
                    document.getElementById('control-brake').classList.add('active')
                    this.brake_mode = true
                    break

                case 'KeyQ':
                    this.rack.up = true
                    document.getElementById('control-lift').classList.add('active')
                    break

                case 'KeyA':
                    this.rack.down = true
                    document.getElementById('control-drop').classList.add('active')
                    break
            }
        })

        // Reset force on keyup
        document.addEventListener('keyup', (event) => {
            switch (event.code) {
                case 'ArrowUp':
                    this.vehicle.applyEngineForce(0, 0)
                    this.vehicle.applyEngineForce(0, 1)
                    document.getElementById('control-up').classList.remove('active')
                    break

                case 'ArrowDown':
                    this.vehicle.applyEngineForce(0, 0)
                    this.vehicle.applyEngineForce(0, 1)
                    document.getElementById('control-down').classList.remove('active')
                    this.back_mode = false
                    break

                case 'ArrowLeft':
                    this.vehicle.setSteeringValue(0, 2)
                    this.vehicle.setSteeringValue(0, 3)
                    document.getElementById('control-left').classList.remove('active')
                    break

                case 'ArrowRight':
                    this.vehicle.setSteeringValue(0, 2)
                    this.vehicle.setSteeringValue(0, 3)
                    document.getElementById('control-right').classList.remove('active')
                    break

                case 'Space':
                    this.vehicle.setBrake(0, 0)
                    this.vehicle.setBrake(0, 1)
                    this.vehicle.setBrake(0, 2)
                    this.vehicle.setBrake(0, 3)
                    document.getElementById('control-brake').classList.remove('active')
                    this.brake_mode = false
                    break

                case 'KeyQ':
                    this.rack.up = false
                    document.getElementById('control-lift').classList.remove('active')
                    break

                case 'KeyA':
                    this.rack.down = false
                    document.getElementById('control-drop').classList.remove('active')
                    break
            }
        })

        // Touch controls
        document.getElementById('control-up').addEventListener('touchstart', () => {
            this.vehicle.applyEngineForce(-this.parameters.maxForce, 2)
            this.vehicle.applyEngineForce(-this.parameters.maxForce, 3)
            document.getElementById('control-up').classList.add('active')

        })

        document.getElementById('control-up').addEventListener('touchend', () => {
            this.vehicle.applyEngineForce(0, 2)
            this.vehicle.applyEngineForce(0, 3)
            document.getElementById('control-up').classList.remove('active')
        })

        document.getElementById('control-down').addEventListener('touchstart', () => {
            this.vehicle.applyEngineForce(this.parameters.maxForce, 2)
            this.vehicle.applyEngineForce(this.parameters.maxForce, 3)
            document.getElementById('control-down').classList.add('active')
            this.back_mode = true
        })

        document.getElementById('control-down').addEventListener('touchend', () => {
            this.vehicle.applyEngineForce(0, 2)
            this.vehicle.applyEngineForce(0, 3)
            document.getElementById('control-down').classList.remove('active')
            this.back_mode = false
        })

        document.getElementById('control-left').addEventListener('touchstart', () => {
            this.vehicle.setSteeringValue(this.parameters.maxSteerVal, 0)
            this.vehicle.setSteeringValue(this.parameters.maxSteerVal, 1)
            document.getElementById('control-left').classList.add('active')
        })

        document.getElementById('control-left').addEventListener('touchend', () => {
            this.vehicle.setSteeringValue(0, 0)
            this.vehicle.setSteeringValue(0, 1)
            document.getElementById('control-left').classList.remove('active')
        })

        document.getElementById('control-right').addEventListener('touchstart', () => {
            this.vehicle.setSteeringValue(-this.parameters.maxSteerVal, 0)
            this.vehicle.setSteeringValue(-this.parameters.maxSteerVal, 1)
            document.getElementById('control-right').classList.add('active')
        })

        document.getElementById('control-right').addEventListener('touchend', () => {
            this.vehicle.setSteeringValue(0, 0)
            this.vehicle.setSteeringValue(0, 1)
            document.getElementById('control-right').classList.remove('active')
        })

        document.getElementById('control-brake').addEventListener('touchstart', () => {
            this.vehicle.setBrake(this.parameters.brakeForce, 0)
            this.vehicle.setBrake(this.parameters.brakeForce, 1)
            this.vehicle.setBrake(this.parameters.brakeForce, 2)
            this.vehicle.setBrake(this.parameters.brakeForce, 3)
            document.getElementById('control-brake').classList.add('active')
            this.break_mode = true
        })

        document.getElementById('control-brake').addEventListener('touchend', () => {
            this.vehicle.setBrake(0, 0)
            this.vehicle.setBrake(0, 1)
            this.vehicle.setBrake(0, 2)
            this.vehicle.setBrake(0, 3)
            document.getElementById('control-brake').classList.remove('active')
            this.break_mode = false
        })

        document.getElementById('control-lift').addEventListener('touchstart', () => {
            this.rack.up = true
            document.getElementById('control-lift').classList.add('active')
        })

        document.getElementById('control-lift').addEventListener('touchend', () => {
            this.rack.up = false
            document.getElementById('control-lift').classList.remove('active')
        })

        document.getElementById('control-drop').addEventListener('touchstart', () => {
            this.rack.down = true
            document.getElementById('control-drop').classList.add('active')
        })

        document.getElementById('control-drop').addEventListener('touchend', () => {
            this.rack.down = false
            document.getElementById('control-drop').classList.remove('active')
        })
    }

    changeColor(color) {
        // Change model color
        this.model.traverse((child) => {
            if (child.isMesh && child.name == 'Cylinder002_3') {
                // Convert color to hex
                child.material.color = new THREE.Color(this.colors[color])
            }
        })
    }

    setColorChoiceListeners() {
        document.querySelector('.color-red').addEventListener('click', () => {
            this.changeColor('red')
            // Remove class active from all colors
            document.querySelectorAll('.color-choice').forEach((element) => {
                element.classList.remove('active')
            })
            // Add active class to the selected color
            document.querySelector('.color-red').classList.add('active')
        })

        document.querySelector('.color-green').addEventListener('click', () => {
            this.changeColor('green')
            // Remove class active from all colors
            document.querySelectorAll('.color-choice').forEach((element) => {
                element.classList.remove('active')
            })
            // Add active class to the selected color
            document.querySelector('.color-green').classList.add('active')
        })

        document.querySelector('.color-blue').addEventListener('click', () => {
            this.changeColor('blue')
            // Remove class active from all colors
            document.querySelectorAll('.color-choice').forEach((element) => {
                element.classList.remove('active')
            })
            // Add active class to the selected color
            document.querySelector('.color-blue').classList.add('active')
        })

        document.querySelector('.color-yellow').addEventListener('click', () => {
            this.changeColor('yellow')
            // Remove class active from all colors
            document.querySelectorAll('.color-choice').forEach((element) => {
                element.classList.remove('active')
            })
            // Add active class to the selected color
            document.querySelector('.color-yellow').classList.add('active')
        })

        // document.getElementById('color-white').addEventListener('click', () => {
        //     this.changeColor('white')
        // })

        // document.getElementById('color-black').addEventListener('click', () => {
        //     this.changeColor('black')
        // })
    }

    update() {
        if (this.rack.up && this.rack.y < this.rack.max_y) {
            // Lift the rack up
            this.rack.y += this.rack.step
            this.chassis.body.removeShape(this.chassis.rack);
            this.chassis.body.addShape(this.chassis.rack, new CANNON.Vec3(this.rack.x, this.rack.y, this.rack.z));
            // Update the body
            this.chassis.body.updateBoundingRadius()
            this.chassis.body.updateMassProperties()

            // Update the model position
            this.model_rack.position.y += this.rack.step * 2 / 3
        }

        if (this.rack.down && this.rack.y > this.rack.min_y) {
            // Lift the rack down
            this.rack.y -= this.rack.step
            this.chassis.body.removeShape(this.chassis.rack);
            this.chassis.body.addShape(this.chassis.rack, new CANNON.Vec3(this.rack.x, this.rack.y, this.rack.z));
            // Update the body
            this.chassis.body.updateBoundingRadius()
            this.chassis.body.updateMassProperties()

            // Update the model position
            this.model_rack.position.y -= this.rack.step * 2 / 3
        }

        if (this.brake_mode) {
            // Change the color of the brake light model
            this.brake_light_light_right.intensity = 40
            this.brake_light_light_left.intensity = 40
            this.brake_light_light_left.color = new THREE.Color(this.brake_color)
            this.brake_light_light_right.color = new THREE.Color(this.brake_color)
        } else if (this.back_mode) {
            // Change the color of the brake light model
            this.brake_light_light_right.intensity = 40
            this.brake_light_light_left.intensity = 40
            this.brake_light_light_left.color = new THREE.Color(this.back_color)
            this.brake_light_light_right.color = new THREE.Color(this.back_color)
        } else {
            // Reset the color of the brake light model
            this.brake_light_light_right.intensity = 0.1
            this.brake_light_light_left.intensity = 0.1
            this.brake_light_light_left.color = new THREE.Color(this.brake_color)
            this.brake_light_light_right.color = new THREE.Color(this.brake_color)
        }

        // Rotate warning light
        this.warning_light_light.intensity = 20
        this.warning_light_light.rotation.y += 0.05

        if (this.experience.dark_mode) {
            this.front_light_light.intensity = 200
        } else {
            this.front_light_light.intensity = 0.2
        }
    }
}